import { useCallback, useEffect, useState } from 'react';

/**
 * En fane er en INSTANS, ikke bare en rute: to faner kan vise den samme side med
 * forskellige parametre (fx "Plan: 19-12-2025" og "Plan: 20-12-2025"). Derfor har
 * hver fane sit eget id, og indholdet holdes mounted, saa tilstanden overlever et
 * faneskift. Mønsteret er taget fra `C:\vibing\nyhbplanner\HBPlanner v2.html`
 * (topTabs/bottomTabs, `hbp-ttabs-*` i localStorage).
 */
export interface TabInstance {
  /** Unikt pr. instans. Gentagne aabninger af samme side genbruger id'et. */
  id: string;
  label: string;
  /** Hvilken slags side fanen viser (se tabs/registry). */
  kind: string;
  /** Parametre til instansen, fx {date: "19-12-2025"}. */
  params?: Record<string, string>;
  /** Ruten fanen svarer til. */
  path?: string;
  closable?: boolean;
}

export interface UseTabsOptions {
  /** localStorage-noegle. Faner gemmes under `<storageKey>-tabs`. */
  storageKey: string;
  /** Faner der altid findes, og som ikke kan lukkes. */
  fixedTabs?: TabInstance[];
  /**
   * Fane der aabnes automatisk, naar den sidste fane lukkes (fx "Kort oversigt").
   * Den kan lukkes som alle andre, saa laenge der er mindst én anden fane aaben,
   * og den er altid den eneste fane, der er tilbage i en tom fanelinje.
   */
  fallbackTab?: TabInstance;
  /** Frasorterer gemte faner, siden deres type ikke længere findes. */
  isValidTab?: (tab: TabInstance) => boolean;
}

export interface TabsApi {
  tabs: TabInstance[];
  activeId: string | null;
  activeTab: TabInstance | null;
  /** Aabner fanen (eller aktiverer den, hvis id'et allerede er aabent). */
  openTab: (tab: TabInstance) => void;
  closeTab: (id: string) => void;
  setActive: (id: string | null) => void;
  /** Lukker alle faner paa naer de faste. */
  closeAll: () => void;
}

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* privat browsertilstand: vi koerer videre uden persistering */
  }
}

/**
 * Fanetilstand med persistering. Bruges baade til top-fanerne i skallen og til
 * bund-/vognfaner inde i en side (samme mønster som `openVehicleTab`,
 * `switchBottomTab` og `closeBottomTab` i referencen).
 */
export function useTabs({
  storageKey,
  fixedTabs = [],
  fallbackTab,
  isValidTab,
}: UseTabsOptions): TabsApi {
  const tabsKey = `${storageKey}-tabs`;
  const activeKey = `${storageKey}-active`;

  /**
   * Gemte faner + de faste. Fallback-fanen tages altid fra koden, saa en gammel
   * gemt udgave (fx med closable: false) ikke laaser fanen fast.
   */
  const restore = (): TabInstance[] => {
    const saved = read<TabInstance[]>(tabsKey, [])
      .filter((tab) => !isValidTab || isValidTab(tab))
      .map((tab) =>
      fallbackTab && tab.id === fallbackTab.id ? { ...fallbackTab } : tab,
    );
    const extra = saved.filter((tab) => !fixedTabs.some((fixed) => fixed.id === tab.id));
    const all = [...fixedTabs, ...extra];
    if (all.length > 0) return all;
    return fallbackTab ? [fallbackTab] : [];
  };

  const [tabs, setTabs] = useState<TabInstance[]>(restore);

  const [activeId, setActiveId] = useState<string | null>(() => {
    const saved = read<string | null>(activeKey, null);
    const all = restore();
    return saved && all.some((tab) => tab.id === saved) ? saved : (all[0]?.id ?? null);
  });

  useEffect(() => {
    write(tabsKey, tabs);
  }, [tabs, tabsKey]);

  useEffect(() => {
    write(activeKey, activeId);
  }, [activeId, activeKey]);

  const openTab = useCallback((tab: TabInstance) => {
    setTabs((current) => {
      const index = current.findIndex((item) => item.id === tab.id);
      if (index === -1) return [...current, tab];
      // Samme fane, nye parametre (fx "Åbn i Kalendere" med et kalender-id):
      // instansen opdateres, saa siden viser det, brugeren bad om.
      const existing = current[index];
      if (JSON.stringify(existing.params ?? {}) === JSON.stringify(tab.params ?? {})) {
        return current;
      }
      const next = [...current];
      next[index] = { ...existing, params: tab.params, path: tab.path };
      return next;
    });
    setActiveId(tab.id);
  }, []);

  const closeTab = useCallback(
    (id: string) => {
      setTabs((current) => {
        const index = current.findIndex((tab) => tab.id === id);
        if (index === -1 || current[index].closable === false) return current;
        // Fallback-fanen kan ikke lukkes, naar den er den eneste tilbage.
        if (fallbackTab && id === fallbackTab.id && current.length === 1) return current;
        const rest = current.filter((tab) => tab.id !== id);
        // Tom fanelinje: fallback-fanen aabnes automatisk igen.
        const next = rest.length === 0 && fallbackTab ? [fallbackTab] : rest;
        setActiveId((active) => {
          if (active !== id) return active;
          // Aktivér naboen: foerst til hoejre, ellers til venstre.
          const neighbour = next[index] ?? next[index - 1] ?? next[0];
          return neighbour?.id ?? null;
        });
        return next;
      });
    },
    [fallbackTab],
  );

  const closeAll = useCallback(() => {
    setTabs((current) => {
      const kept = current.filter((tab) => tab.closable === false);
      const next = kept.length === 0 && fallbackTab ? [fallbackTab] : kept;
      // En fast fane, der allerede var aktiv, forbliver aktiv.
      setActiveId((active) =>
        active && next.some((tab) => tab.id === active) ? active : (next[0]?.id ?? null),
      );
      return next;
    });
  }, [fallbackTab]);

  const activeTab = tabs.find((tab) => tab.id === activeId) ?? null;

  return { tabs, activeId, activeTab, openTab, closeTab, setActive: setActiveId, closeAll };
}
